import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Globe, ChevronDown } from "lucide-react";
import languageConfig from "../config/languageConfig";
import { loadAndSetLanguage } from "../store/reducers/translationReducer";
import { isPublicPathname, getPathWithoutLangPrefix } from "../config/publicRoutes";

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
  background: ${({ theme }) => theme.colors?.light?.background || "#fff"};
  color: ${({ theme }) => theme.colors?.text?.body || "#374151"};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  body.dark & {
    background: ${({ theme }) => theme.colors?.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors?.dark?.border || "hsl(240, 15%, 18%)"};
    color: ${({ theme }) => theme.colors?.dark?.foreground || "#fff"};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors?.primary?.light || "#9b87f5"};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: ${({ theme }) => theme.colors?.light?.background || "#fff"};
  border: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  z-index: 1000;
  overflow: hidden;

  body.dark & {
    background: ${({ theme }) => theme.colors?.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors?.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const Option = styled.button`
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(155, 135, 245, 0.12)" : "transparent")};
  color: ${({ theme }) => theme.colors?.text?.body || "#374151"};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.foreground || "#fff"};
  }

  &:hover {
    background: rgba(155, 135, 245, 0.08);
  }
`;

export function LanguageSwitcher({ variant = "default" }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentLanguage = useSelector((state) => state.translation?.currentLanguage) || i18n.language || languageConfig.defaultLanguage;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    // Always update i18n and localStorage so the UI language actually changes (and persists after reload).
    // Without this, on public routes we only navigated and never set the language, so e.g. switching back to English did nothing.
    dispatch(loadAndSetLanguage(code));
    if (isPublicPathname(pathname)) {
      const publicPath = getPathWithoutLangPrefix(pathname);
      const lang = languageConfig.getLanguage(code);
      const urlCode = lang ? (lang.urlCode || lang.code) : code;
      const isDefault = lang && (lang.default || lang.code === languageConfig.defaultLanguage);
      const newPath = isDefault ? publicPath : `/${urlCode}${publicPath === "/" ? "" : publicPath}`;
      navigate(newPath);
    }
    setOpen(false);
  };

  const current = languageConfig.getLanguage(currentLanguage);

  return (
    <Wrap ref={ref}>
      <Button type="button" onClick={() => setOpen(!open)} aria-label={t("layout.language")}>
        <Globe size={18} />
        <span>{current?.name || currentLanguage}</span>
        <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </Button>
      {open && (
        <Dropdown>
          {languageConfig.supportedLanguages.map((lang) => (
            <Option
              key={lang.code}
              type="button"
              $active={lang.code === currentLanguage}
              onClick={() => handleSelect(lang.code)}
            >
              {lang.nativeName || lang.name}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrap>
  );
}
